// `/meus-quadrinhos/:id` — VER O QUADRINHO COMO QUADRINHO.
//
// T2.43 do pedido, e ele é específico sobre o que falta:
//
//   "Ele tem que me dar a opção de ou eu ver esse quadrinho simplesmente, visualizar como um
//    quadrinho mesmo, ou eu editar ele. Porque as únicas opções que eu tenho hoje é editar ele.
//    EU NÃO CONSIGO VISUALIZAR AQUELE QUADRINHO SEM MEXER NELE, SEM ABRIR AQUELA FEATURE TODA."
//
// "Aquela feature toda" é o editor: menu lateral, submenus, transformadores, botões de camada.
// Para quem só quer ler o que fez — ou mostrar para alguém — isso é ruído, e pior: é ruído que
// deixa arrastar sem querer. Ler e editar são momentos com necessidades opostas, e o editor
// atende só um deles.
//
// ── POR QUE ESTA TELA NÃO É O EDITOR COM OS BOTÕES ESCONDIDOS ───────────────────────────────
//
// Porque esconder botão não impede arrastar. `renderPage({ viewOnly: true })` é outro caminho de
// desenho, e ele já existia: é o mesmo que desenha as miniaturas do acervo. Ali dentro, cada
// item retorna cedo antes de registrar arraste e antes de montar o transformador
// (`comic.class.tsx:309` e `:413`). A página sai como imagem do que foi feito, e não como um
// palco de manipulação sem alças.
//
// A diferença prática: nesta tela, um clique no balão não seleciona nada, e um arraste não move
// nada. É isso que "sem mexer nele" quer dizer.
//
// ── A LARGURA ───────────────────────────────────────────────────────────────────────────────
//
// A folha é escalada para caber na largura da janela, com teto de 900px, medindo a caixa real em
// vez de `window.innerWidth`. O teto existe porque uma página A4 esticada num monitor de 27"
// obriga a mover a cabeça para ler o balão de cima ao de baixo — e o piso de 320 existe porque
// abaixo disso a escala vira um risco, a mesma razão dos pisos do acervo e do seletor de formato.

import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import GuardaDoAcervo from "./guarda";
import Nav from "../../components/nav";
import AppUser from "../../api/app-user.class";
import { quandoNaoHaPagina } from "../../components/quando-nao-ha-pagina";

import "./ver-quadrinho.scss";

const TETO = 900;
const PISO = 320;

/** Mede a caixa onde a folha cabe. O observador olha a caixa ELÁSTICA, nunca a folha — se
 *  olhasse a folha, leria a própria saída e oscilaria. Mesma lição do `useLarguraDaCaixa` dos
 *  motores de leitura. */
function useLarguraDaCaixa(): [React.RefObject<HTMLDivElement>, number] {
  const caixa = useRef<HTMLDivElement>(null);
  const [largura, setLargura] = useState(0);

  useEffect(() => {
    const el = caixa.current;
    if (!el) return;

    const medir = () => {
      const l = el.getBoundingClientRect().width;
      if (l > 0) setLargura(l);
    };
    medir();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", medir);
      return () => window.removeEventListener("resize", medir);
    }
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, []);

  return [caixa, largura];
}

function Leitor({ appUser }: { appUser: AppUser }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caixa, larguraDaCaixa] = useLarguraDaCaixa();

  const comic = appUser.Comics().find((c) => c.getId() === id);

  if (!comic) {
    return (
      <div className="vq-container">
        <Nav />
        <main className="vq-nao-achei">
          <h1>Não encontrei esse quadrinho</h1>
          <p>Ele pode ter sido excluído, ou o endereço pode estar errado.</p>
          <button type="button" onClick={() => navigate("/meus-quadrinhos")}>
            Voltar para meus quadrinhos
          </button>
        </main>
      </div>
    );
  }

  const paginas = comic.Pages();
  const disponivel = Math.max(Math.min(larguraDaCaixa || TETO, TETO), PISO);

  // T10.2 · O MESMO BURACO DO LEITOR DA BIBLIOTECA, e aqui ele é mais fácil de cair: um quadrinho
  // criado e nunca montado tem zero página, e `paginas.map` sobre lista vazia desenha `.vq-folhas`
  // sem nada dentro. O cabeçalho diz "0 páginas" e o resto da tela fica em branco.
  //
  // `quantasORegistroDiz: 0` porque aqui não há ficha separada: a lista de páginas É o registro.
  // Então o único mundo possível neste leitor é o quadrinho incompleto, e a saída é o editor.
  const semPagina = quandoNaoHaPagina({
    quantasChegaram: paginas.length,
    quantasORegistroDiz: 0,
    onde: "acervo",
  });

  return (
    <div className="vq-container">
      <Nav />

      <main className="vq-conteudo">
        <header className="vq-cabecalho">
          <div>
            <h1>{comic.Name() || "Quadrinho sem nome"}</h1>
            <p className="vq-paginas">
              {paginas.length === 1 ? "1 página" : `${paginas.length} páginas`}
            </p>
          </div>

          <div className="vq-acoes">
            <button
              type="button"
              className="vq-editar"
              onClick={() => navigate(`/meus-quadrinhos/${comic.getId()}/editar`)}
            >
              Editar este quadrinho
            </button>
            <button
              type="button"
              className="vq-voltar"
              onClick={() => navigate("/meus-quadrinhos")}
            >
              Voltar para meus quadrinhos
            </button>
          </div>
        </header>

        {semPagina && (
          <div className="vq-sem-pagina" role="status">
            <h2>{semPagina.titulo}</h2>
            <p>{semPagina.explicacao}</p>
            {semPagina.acao && (
              <button
                type="button"
                onClick={() => navigate(`/meus-quadrinhos/${comic.getId()}/editar`)}
              >
                {semPagina.acao.rotulo}
              </button>
            )}
          </div>
        )}

        <div className="vq-folhas" ref={caixa}>
          {paginas.map((pagina, i) => {
            const escala = disponivel / pagina.width;
            return (
              <figure className="vq-folha" key={i}>
                <div
                  className="vq-folha-palco"
                  style={{ width: disponivel, height: pagina.height * escala }}
                >
                  {comic.renderPage(i, {
                    width: disponivel,
                    height: pagina.height * escala,
                    viewOnly: true,
                  })}
                </div>
                {paginas.length > 1 && (
                  <figcaption>
                    Página {i + 1} de {paginas.length}
                  </figcaption>
                )}
              </figure>
            );
          })}
        </div>
      </main>
    </div>
  );
}

function VerQuadrinho() {
  return (
    <GuardaDoAcervo>{({ appUser }) => <Leitor appUser={appUser} />}</GuardaDoAcervo>
  );
}

export default VerQuadrinho;
